import { useState } from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, useTypedT } from "@racoon/i18n";
import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  GroupChevron,
  Switch,
} from "@racoon/ui";
import { ModeTabs } from "~/shared/ui/mode-tabs";
import { useNotificationSettings } from "../../model/useNotificationSettings";
import { CHANNEL_COLUMNS, CUSTOMER_EVENT_GROUPS, eventDraft } from "../../model/notification-rules";

const { UserRole, NotificationEvent, NotificationChannel } = ConnectProto;

// Every role the backend's NotificationRule.audience will accept, in the design's order.
// Only the customer one has rules on the backend so far.
const ROLES = [
  { role: UserRole.PROVIDER_DISPATCHER, label: "notifications.roles.dispatcher" },
  { role: UserRole.PROVIDER_MECHANIC, label: "notifications.roles.mechanic" },
  { role: UserRole.PROVIDER_SALES, label: "notifications.roles.sales" },
  { role: UserRole.PROVIDER_OWNER, label: "notifications.roles.owner" },
  { role: UserRole.DRIVER, label: "notifications.roles.driver" },
  { role: UserRole.RECEIVER_USER, label: "notifications.roles.receiver" },
] as const;
const SUPPORTED_ROLE = UserRole.RECEIVER_USER;

const GROUP_LABELS = {
  jobs: "notifications.groups.jobs",
  requests: "notifications.groups.requests",
  contracts: "notifications.groups.contracts",
} as const;

const EVENT_LABELS = {
  [NotificationEvent.JOB_ASSIGNED]: {
    title: "notifications.events.JOB_ASSIGNED.title",
    description: "notifications.events.JOB_ASSIGNED.description",
  },
  [NotificationEvent.JOB_ENROUTE]: {
    title: "notifications.events.JOB_ENROUTE.title",
    description: "notifications.events.JOB_ENROUTE.description",
  },
  [NotificationEvent.JOB_COMPLETED]: {
    title: "notifications.events.JOB_COMPLETED.title",
    description: "notifications.events.JOB_COMPLETED.description",
  },
  [NotificationEvent.JOB_CREATED]: {
    title: "notifications.events.JOB_CREATED.title",
    description: "notifications.events.JOB_CREATED.description",
  },
  [NotificationEvent.CONTRACT_EXPIRING]: {
    title: "notifications.events.CONTRACT_EXPIRING.title",
    description: "notifications.events.CONTRACT_EXPIRING.description",
  },
  [NotificationEvent.WORK_ACT_AND_INVOICE_ISSUED]: {
    title: "notifications.events.WORK_ACT_AND_INVOICE_ISSUED.title",
    description: "notifications.events.WORK_ACT_AND_INVOICE_ISSUED.description",
  },
} as const;

const CHANNEL_LABELS = {
  [NotificationChannel.PUSH]: {
    title: "notifications.columns.push",
    hint: "notifications.columns.pushHint",
  },
  [NotificationChannel.WHATSAPP]: {
    title: "notifications.columns.whatsapp",
    hint: "notifications.columns.whatsappHint",
  },
  [NotificationChannel.EMAIL]: {
    title: "notifications.columns.email",
    hint: "notifications.columns.emailHint",
  },
} as const;

const SECONDARY = "text-text-subtle";
const CELL = "flex w-40 shrink-0 items-center justify-center p-2";

interface NotificationsSettingsProps {
  companyId?: number;
}

export function NotificationsSettings({ companyId }: NotificationsSettingsProps) {
  const t = useTypedT(Namespace.Settings);
  const [role, setRole] = useState<ConnectProto.UserRole>(SUPPORTED_ROLE);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const { rules, isReady, isLoading, isSaving, toggleChannel, setOptOut } = useNotificationSettings(
    companyId,
    role,
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="w-[720px]">
        <ModeTabs
          value={String(role)}
          onChange={(value) => setRole(Number(value))}
          options={ROLES.map(({ role: value, label }) => {
            const supported = value === SUPPORTED_ROLE;
            return {
              value: String(value),
              label: t(label),
              disabled: !supported,
              tooltip: supported ? undefined : t("notifications.soon"),
            };
          })}
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex h-12 overflow-hidden rounded-lg border border-slate-200 text-xs font-bold uppercase leading-4">
          <div
            className={`flex w-10 shrink-0 items-center justify-center border-r border-slate-200 ${SECONDARY}`}
          >
            #
          </div>
          <div className={`flex flex-1 items-center border-r border-slate-200 px-2 ${SECONDARY}`}>
            {t("notifications.columns.event")}
          </div>
          {CHANNEL_COLUMNS.map((channel) => (
            <ColumnTitle
              key={channel}
              title={t(CHANNEL_LABELS[channel].title)}
              hint={t(CHANNEL_LABELS[channel].hint)}
            />
          ))}
          <ColumnTitle
            title={t("notifications.columns.optOut")}
            hint={t("notifications.columns.optOutHint")}
            last
          />
        </div>

        {!isReady ? (
          // Failed or skipped: an empty table would read as "every notification is off".
          <div className="py-10 text-center text-gray-500">
            {t(isLoading ? "messages.loading" : "messages.loadError")}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {CUSTOMER_EVENT_GROUPS.map((group) => (
              <Collapsible
                key={group.key}
                open={!collapsed.has(group.key)}
                onOpenChange={(open) =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (open) next.delete(group.key);
                    else next.add(group.key);
                    return next;
                  })
                }
                className="overflow-hidden rounded-lg border border-slate-200 bg-white"
              >
                <CollapsibleTrigger className="flex h-12 w-full items-center gap-4 bg-slate-100 px-4 text-left">
                  <GroupChevron isExpanded={!collapsed.has(group.key)} />
                  <span className="text-base font-semibold leading-4 tracking-[-0.01px] text-text-body">
                    {t(GROUP_LABELS[group.key])}
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {group.events.map((event, index) => {
                    const { channels, optOutAllowed } = eventDraft(rules, event);
                    const enabled = channels.length > 0;
                    const label = EVENT_LABELS[event];
                    return (
                      <div key={event} className="flex border-t border-slate-200">
                        <div className="flex w-10 shrink-0 items-center justify-center px-2 py-3 text-sm leading-4 text-text-body">
                          {index + 1}
                        </div>
                        <div className="flex min-h-14 min-w-0 flex-1 flex-col justify-center gap-1 p-2 font-medium leading-4">
                          <span className="text-sm tracking-[-0.2px] text-text-body">
                            {t(label.title)}
                          </span>
                          <span className={`text-xs tracking-[-0.01px] ${SECONDARY}`}>
                            {t(label.description)}
                          </span>
                        </div>
                        {CHANNEL_COLUMNS.map((channel) => (
                          <div key={channel} className={CELL}>
                            <Switch
                              id={`notification-${event}-${channel}`}
                              aria-label={`${t(label.title)}: ${t(CHANNEL_LABELS[channel].title)}`}
                              checked={channels.includes(channel)}
                              disabled={isSaving}
                              onCheckedChange={() => toggleChannel(event, channel)}
                            />
                          </div>
                        ))}
                        <div className={CELL}>
                          <Checkbox
                            id={`notification-${event}-opt-out`}
                            aria-label={`${t(label.title)}: ${t("notifications.columns.optOut")} ${t("notifications.columns.optOutHint")}`}
                            checked={enabled && optOutAllowed}
                            disabled={!enabled || isSaving}
                            onCheckedChange={(checked) => setOptOut(event, checked === true)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ColumnTitleProps {
  title: string;
  hint: string;
  last?: boolean;
}

function ColumnTitle({ title, hint, last }: ColumnTitleProps) {
  return (
    <div
      className={`flex w-40 shrink-0 flex-col items-center justify-center gap-1 px-2 ${SECONDARY} ${last ? "" : "border-r border-slate-200"}`}
    >
      <span>{title}</span>
      <span className="text-[10px] font-semibold normal-case leading-[10px] tracking-[-0.05px]">
        {hint}
      </span>
    </div>
  );
}
